import {
  ActionList,
  ActionListItem,
  Dropdown,
  DropdownOverlay,
  FilterChipGroup,
  FilterChipSelectInput,
  ListViewFilters,
} from '@razorpay/blade/components';
import {
  earningFiltersMap,
  earningPayTypeFilters,
  statusFilters,
  taxabilityFilters,
} from 'components/SalaryComponents/constants';
import { EarningPayType, EarningPayTypeWithoutNonPayableBenefits } from 'components/SalaryComponents/types';
import { getEarningPayTypes } from 'components/WizardViews/SalaryComponents/constants';
import useFeatures from 'hooks/useFeatures';
import { useFilters } from 'hooks/useFilters';
import { PaginationFiltersMap } from 'hooks/usePagination';
import React from 'react';
import StatusFilter from './StatusFilter';

const EarningsFilters = ({ counts }: { counts?: Record<keyof typeof statusFilters, number> }) => {
  const { currentFilters, applyFilter, removeFilter, getSelectedFiltersCount } = useFilters({
    filtersMap: earningFiltersMap,
    multipleValueFilters: [earningFiltersMap.taxability],
  });
  const { features } = useFeatures();

  const selectedFiltersCount = getSelectedFiltersCount(currentFilters, [
    PaginationFiltersMap.limit,
    PaginationFiltersMap.page,
    earningFiltersMap.status,
  ]);

  const earningPayTypes = getEarningPayTypes(features);
  const payType = currentFilters[earningFiltersMap.payType];
  const selectedPayType = payType in earningPayTypeFilters ? payType : '';
  const taxability = currentFilters[earningFiltersMap.taxability] as string[];
  const selectedTaxability = [...new Set(taxability)].filter((item) => item in taxabilityFilters);

  return (
    <>
      <ListViewFilters
        quickFilters={<StatusFilter counts={counts} />}
        selectedFiltersCount={selectedFiltersCount}>
        <FilterChipGroup
          onClearButtonClick={() =>
            removeFilter([earningFiltersMap.payType, earningFiltersMap.taxability])
          }>
          <Dropdown selectionType="single">
            <FilterChipSelectInput
              onClearButtonClick={() => removeFilter(earningFiltersMap.payType)}
              name={earningFiltersMap.payType}
              label="Pay Type"
              value={selectedPayType}
              onChange={({ values }) => {
                if (values.length === 0) {
                  removeFilter(earningFiltersMap.payType);
                } else {
                  applyFilter(earningFiltersMap.payType, values[0]);
                }
              }}
              accessibilityLabel="pay type filter"
            />
            <DropdownOverlay>
              <ActionList>
                {Object.entries(earningPayTypes).map(([payTypeKey, payTypeValue]) => {
                  return (
                    <ActionListItem
                      key={payTypeKey}
                      title={earningPayTypeFilters[payTypeKey as EarningPayTypeWithoutNonPayableBenefits]}
                      value={payTypeKey}
                      testID={`pay type option ${earningPayTypeFilters[
                        payTypeKey as EarningPayTypeWithoutNonPayableBenefits
                      ].toLowerCase()}`}
                      isDisabled={payTypeValue.isComingSoon}
                    />
                  );
                })}
              </ActionList>
            </DropdownOverlay>
          </Dropdown>
          <Dropdown selectionType="multiple">
            <FilterChipSelectInput
              onClearButtonClick={() => removeFilter(earningFiltersMap.taxability)}
              name={earningFiltersMap.taxability}
              label="Taxability"
              value={selectedTaxability}
              onChange={({ values }) => {
                if (values.length === 0) {
                  removeFilter(earningFiltersMap.taxability);
                } else {
                  applyFilter(earningFiltersMap.taxability, values);
                }
              }}
              accessibilityLabel="taxability filter"
            />
            <DropdownOverlay>
              <ActionList>
                {Object.entries(taxabilityFilters).map(([filterKey, filterValue]) => {
                  return (
                    <ActionListItem
                      key={filterKey}
                      title={filterValue}
                      value={filterKey}
                      testID={filterValue}
                    />
                  );
                })}
              </ActionList>
            </DropdownOverlay>
          </Dropdown>
        </FilterChipGroup>
      </ListViewFilters>
    </>
  );
};

export default EarningsFilters;
